import type {
  BorderProps,
  ResponsiveValue,
  ShadowProps,
  TypographyProps,
} from 'styled-system';
import { border, shadow, typography } from 'styled-system';

import type { BorderOverrideProps } from '../extensions';
import { borderShorthandOverride } from '../extensions';
import styledFactory from '../styledFactory';
import variantFactory from '../variantFactory';

export interface Props
  extends Omit<BorderProps, keyof BorderOverrideProps>,
    ShadowProps,
    TypographyProps,
    BorderOverrideProps {
  variant?: ResponsiveValue<string>;
  __textInputCommon__?: 'common';
  __textInputComponentCommon__?: 'common';
}

export type InputProps = Props & React.ComponentPropsWithoutRef<'input'>;
export type TextAreaProps = Props & React.ComponentPropsWithoutRef<'textarea'>;

const extensions = [border, shadow, typography, borderShorthandOverride];

const defaultProps = {
  variant: 'default',
  __textInputCommon__: 'common',
  __textInputComponentCommon__: 'common',
} satisfies Partial<Props>;

/**
 * TextInput is a component that renders a styled input element.
 */
const TextInput = styledFactory<InputProps, 'input'>(
  'input',
  'TextInput',
  [
    variantFactory('components.textInput', {
      prop: '__textInputCommon__',
    }),
    variantFactory('components.textInput.line', {
      prop: '__textInputComponentCommon__',
    }),
    variantFactory('components.textInput.line.variants'),
  ],
  extensions,
  undefined,
  {
    ...defaultProps,
    type: 'text',
  },
);

/**
 * TextArea is a component that renders a styled textarea element.
 */
const TextArea = styledFactory<TextAreaProps, 'textarea'>(
  'textarea',
  'TextInputArea',
  [
    variantFactory('components.textInput', {
      prop: '__textInputCommon__',
    }),
    variantFactory('components.textInput.area', {
      prop: '__textInputComponentCommon__',
    }),
    variantFactory('components.textInput.area.variants'),
  ],
  extensions,
  undefined,
  defaultProps,
);

export default Object.assign(TextInput, { area: TextArea });
